import { type FC, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import copy from "copy-to-clipboard";
import CopyIcon from "@mui/icons-material/FileCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { LocalStorage } from "@/shared/utils/localstorage";

export interface EmbedCodeProps {
  embedUrl?: string | null;
}

interface PlayerOptions {
  autoPlay: boolean;
  loop: boolean;
  muted: boolean;
}

const storage = LocalStorage("embedCode");

// Клон референсного partner/utils/playerUrl.js — добавляет параметры плеера
// в URL страницы, на которую ссылается embed-код.
const buildPlayerUrl = (embedUrl: string, options: PlayerOptions): string => {
  const url = new URL(embedUrl);
  if (options.autoPlay) url.searchParams.set("autoplay", "1");
  if (options.loop) url.searchParams.set("loop", "1");
  if (options.muted) url.searchParams.set("muted", "1");
  return url.href;
};

// Клон референсного partner/utils/embed.js: responsive — обёртка с
// paddingTop:56.25% (16:9), иначе — iframe фиксированного размера.
const buildEmbedHtml = (playerUrl: string, responsive: boolean): string => {
  if (responsive) {
    return `<div style='overflow:hidden;padding-top:56.25%;position:relative;width:100%;'><iframe src='${playerUrl}' referrerpolicy="unsafe-url" frameborder='0' allow='autoplay; encrypted-media' allowfullscreen style='height:100%;left:0;position:absolute;top:0;width:100%;'></iframe></div>`;
  }
  return `<iframe style='width:854px;height:480px;' src='${playerUrl}' referrerpolicy="unsafe-url" frameborder='0' allow='autoplay; encrypted-media' allowfullscreen></iframe>`;
};

export const EmbedCode: FC<EmbedCodeProps> = ({ embedUrl }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [autoPlay, setAutoPlay] = useState(() =>
    storage.getItemOrDefault("autoPlay", true)
  );
  const [loop, setLoop] = useState(() =>
    storage.getItemOrDefault("loop", true)
  );
  const [muted, setMuted] = useState(() =>
    storage.getItemOrDefault("muted", true)
  );
  const [responsive, setResponsive] = useState(() =>
    storage.getItemOrDefault("responsive", true)
  );

  if (!embedUrl) return null;

  const playerUrlWithParams = buildPlayerUrl(embedUrl, {
    autoPlay,
    loop,
    muted,
  });
  const embedHtml = buildEmbedHtml(playerUrlWithParams, responsive);

  const handleCopy = () => {
    copy(embedHtml);
    enqueueSnackbar(t("videos.embedCode.copiedSnackbar"), {
      variant: "success",
    });
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid size={12}>
        <TextField
          fullWidth
          value={embedHtml}
          label={t("videos.embedCode.label")}
          variant="outlined"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  href={playerUrlWithParams}
                  target="_blank"
                  size="large"
                >
                  <OpenInNewIcon />
                </IconButton>
                <IconButton onClick={handleCopy} size="large">
                  <CopyIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Switch
              checked={autoPlay}
              onChange={() => {
                setAutoPlay(!autoPlay);
                storage.setItem("autoPlay", !autoPlay);
              }}
            />
          }
          label={t("videos.embedCode.autoplayLabel")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={loop}
              onChange={() => {
                setLoop(!loop);
                storage.setItem("loop", !loop);
              }}
            />
          }
          label={t("videos.embedCode.loopLabel")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={muted}
              onChange={() => {
                setMuted(!muted);
                storage.setItem("muted", !muted);
              }}
            />
          }
          label={t("videos.embedCode.mutedLabel")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={responsive}
              onChange={() => {
                setResponsive(!responsive);
                storage.setItem("responsive", !responsive);
              }}
            />
          }
          label={t("videos.embedCode.responsiveLabel")}
        />
      </Grid>
    </Grid>
  );
};
